import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, Mail, ChevronDown, ChevronRight, Zap, Headphones, Palette, Globe, ClipboardList, Lightbulb } from "lucide-react";
import globaltisLogo from "@/assets/globaltis-ia-logo.png";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { capsulas } from "@/data/capsulas";
import { getAllClassifiedTypes } from "@/utils/businessClassifier";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCapsulasOpen, setIsCapsulasOpen] = useState(false);
  const [isServiciosOpen, setIsServiciosOpen] = useState(false);
  const [isNegociosOpen, setIsNegociosOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Extraer tipos de negocios para el menú
  const { businessEntries } = getAllClassifiedTypes();
  
  // Tipos de negocio con página de conversión dedicada
  const negociosConPagina = [
    'Academias', 'Agencias', 'Asesorías', 'Clínicas', 'Consultoras', 
    'Despachos', 'Ecommerce', 'Franquicias', 'Gestorías', 'Gimnasios', 
    'Hoteles', 'Inmobiliarias', 'Restaurantes', 'Tiendas'
  ];

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(progress);

      // Control de visibilidad del header
      if (scrolled < 10) {
        // Mostrar header si estamos en el top
        setIsVisible(true);
      } else if (scrolled > lastScrollY) {
        // Scrolling hacia abajo - ocultar header
        setIsVisible(false);
      } else if (scrolled < lastScrollY) {
        // Scrolling hacia arriba - mostrar header
        setIsVisible(true);
      }
      
      setLastScrollY(scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleNavigation = (section: string, sectionId?: string) => {
    setIsOpen(false);
    setIsNegociosOpen(false);
    
    if (sectionId && location.pathname === section) {
      // Ya estamos en la página, solo hacer scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else if (sectionId && location.pathname === "/") {
      // Scroll to section on homepage
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else if (section === "/" && location.pathname === "/") {
      // Already on home, just scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (sectionId && location.pathname !== section) {
      // Navigate to page first, then scroll
      navigate(section);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } else {
      // Regular navigation
      navigate(section);
    }
  };


  const navItems = [
    { label: "Inicio", path: "/", sectionId: undefined },
    { label: "Negocios", path: "/negocios", sectionId: undefined },
    { label: "News", path: "/blog", sectionId: undefined },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isHomepage = location.pathname === "/";
  const headerBgClass = isHomepage 
    ? "bg-gradient-to-b from-black/40 via-black/20 to-transparent" 
    : "bg-white/95 dark:bg-black/95";
  const navBgClass = isHomepage
    ? "bg-gradient-to-b from-black/40 via-black/20 to-transparent"
    : "bg-white/80 dark:bg-black/80";
  const textColorClass = isHomepage
    ? "text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]"
    : "text-foreground";

  return (
    <header className={`sticky top-0 z-50 w-full transition-transform duration-300 ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      {/* Scroll Progress Indicator */}
      <div className="absolute top-0 left-0 h-[3px] bg-border/30 w-full z-10">
        <div 
          className="h-full bg-gradient-to-r from-primary via-primary/90 to-primary transition-all duration-150 ease-out shadow-sm shadow-primary/50"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      
      {/* Contenedor unificado para header */}
      <div className={`${headerBgClass} backdrop-blur-sm transition-colors duration-300`}>
        {/* Top Bar - Logo & Contact Info */}
        <div className="hidden lg:flex w-full">
        <div className="container flex justify-between items-center px-4 lg:px-8 py-4">
          
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={globaltisLogo} 
              alt="Globaltis IA" 
              className="h-20 w-auto object-contain"
            />
          </Link>
          
          {/* Contact Info */}
          <div className="flex items-center gap-6">
            <a 
              href="tel:+34916651232" 
              className={`flex items-center gap-2 text-sm transition-colors ${
                isHomepage 
                  ? 'text-white/90 hover:text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Phone size={16} />
              <span>+34 916 651 232</span>
            </a>
            <a 
              href="mailto:soporte@globaltis.com" 
              className={`flex items-center gap-2 text-sm transition-colors ${
                isHomepage 
                  ? 'text-white/90 hover:text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Mail size={16} />
              <span>soporte@globaltis.com</span>
            </a>
          </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="w-full">
          <div className="container flex h-20 items-center justify-between px-4 lg:px-8">
        
        {/* Logo Mobile */}
        <Link to="/" className="flex lg:hidden items-center">
          <img 
            src={globaltisLogo} 
            alt="Globaltis IA" 
            className="h-12 sm:h-16 w-auto object-contain"
          />
        </Link>
        
        {/* Desktop Navigation - Centered */}
        <NavigationMenu className="hidden lg:flex flex-1 justify-center ml-[4cm]">
          <NavigationMenuList className="space-x-1 xl:space-x-4">
            <NavigationMenuItem>
              <button
                onClick={() => handleNavigation("/", undefined)}
                className={cn(
                  navigationMenuTriggerStyle(),
                  "text-base font-bold bg-transparent",
                  isHomepage && "text-white hover:text-white/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]"
                )}
              >
                Inicio
              </button>
            </NavigationMenuItem>

            {/* Servicios with dropdown */}
            <NavigationMenuItem>
              <NavigationMenuTrigger className={cn(
                "text-base font-bold bg-transparent",
                isHomepage && "text-white hover:text-white/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]"
              )}>
                Servicios
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid grid-cols-3 w-[750px] gap-2 p-2 bg-popover border border-border shadow-lg rounded-lg z-50">
                  <li className="col-span-3">
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/servicios", undefined)}
                        className="group block select-none space-y-1 rounded-md p-2 leading-none no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-bold transition-colors duration-300">Ver todos los servicios</div>
                        <p className="text-xs leading-snug text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Explora todas nuestras soluciones
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  <li className="col-span-3 border-t border-border"></li>
                  <li>
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/servicios/automatizacion", undefined)}
                        className="group block select-none rounded-md p-2 leading-snug no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-semibold mb-1 transition-colors duration-300"><Zap className="inline-block w-3 h-3 mr-1" /> Automatización de Procesos</div>
                        <p className="text-xs text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Emails, facturas y flujos que trabajan solos
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/servicios/atencion-cliente", undefined)}
                        className="group block select-none rounded-md p-2 leading-snug no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-semibold mb-1 transition-colors duration-300"><Headphones className="inline-block w-3 h-3 mr-1" /> Atención al Cliente 24/7</div>
                        <p className="text-xs text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Agentes de voz y WhatsApp que nunca duermen
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/servicios/contenido-ia", undefined)}
                        className="group block select-none rounded-md p-2 leading-snug no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-semibold mb-1 transition-colors duration-300"><Palette className="inline-block w-3 h-3 mr-1" /> Contenido Profesional con IA</div>
                        <p className="text-xs text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Videos, presentaciones y diseños automatizados
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/servicios/presencia-digital", undefined)}
                        className="group block select-none rounded-md p-2 leading-snug no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-semibold mb-1 transition-colors duration-300"><Globe className="inline-block w-3 h-3 mr-1" /> Presencia Digital Express</div>
                        <p className="text-xs text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Webs y landing pages creadas con IA en días
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/servicios/gestion-administrativa", undefined)}
                        className="group block select-none rounded-md p-2 leading-snug no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-semibold mb-1 transition-colors duration-300"><ClipboardList className="inline-block w-3 h-3 mr-1" /> Gestión Administrativa Digital</div>
                        <p className="text-xs text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Bases de conocimiento y actas automáticas
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/servicios/diagnostico-ia", undefined)}
                        className="group block select-none rounded-md p-2 leading-snug no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-semibold mb-1 transition-colors duration-300"><Lightbulb className="inline-block w-3 h-3 mr-1" /> Diagnóstico de Madurez IA</div>
                        <p className="text-xs text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Auditoría y hoja de ruta personalizada
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Negocios with dropdown */}
            <NavigationMenuItem>
              <NavigationMenuTrigger className={cn(
                "text-base font-bold bg-transparent",
                isHomepage && "text-white hover:text-white/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]"
              )}>
                Negocios
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid grid-cols-4 w-[900px] gap-2 p-2 bg-popover border border-border shadow-lg rounded-lg z-50">
                  <li className="col-span-4">
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/negocios", undefined)}
                        className="group block select-none space-y-1 rounded-md p-2 leading-none no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-bold transition-colors duration-300">Ver todos los tipos de negocio</div>
                        <p className="text-xs text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Explora soluciones por sector
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  
                  <li className="col-span-4 border-t border-border"></li>
                  
                  {businessEntries.length > 0 && (
                    <>
                      <li className="col-span-4 px-2 py-1">
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase">
                          Tipos de Negocio
                        </div>
                      </li>
                      {businessEntries.map(([businessType]) => {
                        const hasDetailPage = negociosConPagina.includes(businessType);
                        
                        return (
                          <li key={businessType}>
                            <NavigationMenuLink asChild>
                              {hasDetailPage ? (
                                <Link
                                  to={`/negocios/${businessType.toLowerCase()}`}
                                  className="block select-none rounded-md p-2 leading-snug transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                                >
                                  <div className="text-xs font-medium transition-colors duration-300">{businessType}</div>
                                </Link>
                              ) : (
                                <button
                                  onClick={() => handleNavigation(
                                    "/negocios", 
                                    businessType.toLowerCase().replace(/\s+/g, '-')
                                  )}
                                  className="block select-none rounded-md p-2 leading-snug transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                                >
                                  <div className="text-xs font-medium transition-colors duration-300">{businessType}</div>
                                </button>
                              )}
                            </NavigationMenuLink>
                          </li>
                        );
                      })}
                    </>
                  )}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Cápsulas with dropdown organized by categories */}
            <NavigationMenuItem>
              <NavigationMenuTrigger className={cn(
                "text-base font-bold bg-transparent",
                isHomepage && "text-white hover:text-white/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]"
              )}>
                Herramientas
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid grid-cols-4 w-[900px] gap-2 p-2 bg-popover border border-border shadow-lg rounded-lg z-50">
                  <li className="col-span-4">
                    <NavigationMenuLink asChild>
                      <button
                        onClick={() => handleNavigation("/capsulas")}
                        className="group block select-none space-y-1 rounded-md p-2 leading-none no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                      >
                        <div className="text-xs font-bold transition-colors duration-300">Ver todas las herramientas</div>
                        <p className="text-xs leading-snug text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">
                          Explora soluciones de IA por tema
                        </p>
                      </button>
                    </NavigationMenuLink>
                  </li>
                  <li className="col-span-4 border-t border-border"></li>
                  {capsulas.slice(0, 12).map((capsula) => (
                    <li key={capsula.id}>
                      <NavigationMenuLink asChild>
                        <button
                          onClick={() => handleNavigation(`/capsulas/${capsula.id}`)}
                          className="group block select-none rounded-md p-2 leading-snug no-underline outline-none transition-all duration-300 ease-out hover:bg-gray-100 hover:shadow-sm dark:hover:bg-gray-800/50 w-full text-left"
                        >
                          <div className="text-xs font-medium transition-colors duration-300">
                            <capsula.icon className="inline-block w-3 h-3 mr-1" />
                            <span>{capsula.title}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300 mt-1">
                            {capsula.description}
                          </p>
                        </button>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>


            <NavigationMenuItem>
              <button
                onClick={() => handleNavigation("/cursos", undefined)}
                className={cn(
                  navigationMenuTriggerStyle(),
                  "text-base font-bold bg-transparent",
                  isHomepage && "text-white hover:text-white/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]"
                )}
              >
                Guías
              </button>
            </NavigationMenuItem>


            <NavigationMenuItem>
              <button
                onClick={() => handleNavigation("/blog", undefined)}
                className={cn(
                  navigationMenuTriggerStyle(),
                  "text-base font-bold bg-transparent",
                  isHomepage && "text-white hover:text-white/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]"
                )}
              >
                News
              </button>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* CTA Button - Desktop */}
        <Button 
          variant="hero" 
          className="hidden lg:flex text-sm xl:text-base"
          onClick={() => navigate('/servicios/diagnostico-ia')}
        >
          Diagnóstico Gratis
        </Button>

        {/* Mobile Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button 
              variant="ghost" 
              size="icon"
              className={isHomepage ? "text-white hover:text-white/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]" : ""}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Abrir menú</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[380px]">
            <div className="flex flex-col h-full overflow-y-auto space-y-4 mt-8">
              
              <nav className="flex flex-col space-y-2">
                {/* Inicio button */}
                <button
                  onClick={() => handleNavigation("/", undefined)}
                   className={`text-left px-4 py-2.5 rounded-md font-medium transition-colors ${
                    isActive("/")
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:bg-secondary"
                  }`}
                >
                  Inicio
                </button>

                {/* Servicios Collapsible for Mobile */}
                <Collapsible open={isServiciosOpen} onOpenChange={setIsServiciosOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      className="text-left px-4 py-2.5 rounded-md font-medium transition-colors flex items-center justify-between w-full text-foreground hover:bg-secondary"
                    >
                      Servicios
                      <ChevronDown className={`h-4 w-4 transition-transform ${isServiciosOpen ? "rotate-180" : ""}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 mt-1 space-y-1">
                    <button
                      onClick={() => handleNavigation("/servicios", undefined)}
                      className="text-left px-4 py-2 rounded-md text-sm font-medium text-foreground hover:bg-secondary w-full"
                    >
                      Ver todos los servicios
                    </button>
                    <button
                      onClick={() => handleNavigation("/servicios/automatizacion", undefined)}
                      className="text-left px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary w-full whitespace-normal leading-snug"
                    >
                      <Zap className="inline-block w-3 h-3 mr-1" /> Automatización de Procesos
                    </button>
                    <button
                      onClick={() => handleNavigation("/servicios/atencion-cliente", undefined)}
                      className="text-left px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary w-full whitespace-normal leading-snug"
                    >
                      <Headphones className="inline-block w-3 h-3 mr-1" /> Atención al Cliente 24/7
                    </button>
                    <button
                      onClick={() => handleNavigation("/servicios/contenido-ia", undefined)}
                      className="text-left px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary w-full whitespace-normal leading-snug"
                    >
                      <Palette className="inline-block w-3 h-3 mr-1" /> Contenido Profesional con IA
                    </button>
                    <button
                      onClick={() => handleNavigation("/servicios/presencia-digital", undefined)}
                      className="text-left px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary w-full whitespace-normal leading-snug"
                    >
                      <Globe className="inline-block w-3 h-3 mr-1" /> Presencia Digital Express
                    </button>
                    <button
                      onClick={() => handleNavigation("/servicios/gestion-administrativa", undefined)}
                      className="text-left px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary w-full whitespace-normal leading-snug"
                    >
                      <ClipboardList className="inline-block w-3 h-3 mr-1" /> Gestión Administrativa Digital
                    </button>
                    <button
                      onClick={() => handleNavigation("/servicios/diagnostico-ia", undefined)}
                      className="text-left px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary w-full whitespace-normal leading-snug"
                    >
                      <Lightbulb className="inline-block w-3 h-3 mr-1" /> Diagnóstico de Madurez IA
                    </button>
                  </CollapsibleContent>
                </Collapsible>

                {/* Negocios Collapsible for Mobile */}
                <Collapsible
                  open={isNegociosOpen}
                  onOpenChange={setIsNegociosOpen}
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-2.5 text-base font-medium hover:bg-accent/50 transition-colors rounded-md">
                    <span>Negocios</span>
                    <ChevronRight
                      className={`h-5 w-5 transition-transform ${
                        isNegociosOpen ? "rotate-90" : ""
                      }`}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-6 space-y-1">
                    <button
                      onClick={() => handleNavigation("/negocios", undefined)}
                      className="block w-full text-left px-4 py-2.5 text-sm hover:bg-accent/50 transition-colors rounded-md"
                    >
                      Ver todos los tipos de negocio
                    </button>
                    
                    {businessEntries.length > 0 && (
                      <>
                        <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">
                          Tipos de Negocio
                        </div>
                        {businessEntries.map(([businessType]) => {
                          const hasDetailPage = negociosConPagina.includes(businessType);
                          
                          return hasDetailPage ? (
                            <Link
                              key={businessType}
                              to={`/negocios/${businessType.toLowerCase()}`}
                              onClick={() => setIsOpen(false)}
                              className="block w-full text-left px-4 py-2.5 text-sm hover:bg-accent/50 transition-colors rounded-md"
                            >
                              {businessType}
                            </Link>
                          ) : (
                            <button
                              key={businessType}
                              onClick={() => handleNavigation(
                                "/negocios",
                                businessType.toLowerCase().replace(/\s+/g, '-')
                              )}
                              className="block w-full text-left px-4 py-2.5 text-sm hover:bg-accent/50 transition-colors rounded-md"
                            >
                              {businessType}
                            </button>
                          );
                        })}
                      </>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Other navigation items */}
                {navItems.filter(item => item.label !== "Negocios").slice(1).map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleNavigation(item.path, item.sectionId)}
                    className={`text-left px-4 py-2.5 rounded-md font-medium transition-colors ${
                      isActive(item.path)
                        ? "text-primary bg-primary/10"
                        : "text-foreground hover:bg-secondary"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                
                {/* Cápsulas Collapsible for Mobile */}
                <Collapsible open={isCapsulasOpen} onOpenChange={setIsCapsulasOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                     className={`text-left px-4 py-2.5 rounded-md font-medium transition-colors flex items-center justify-between w-full ${
                        isActive("/capsulas")
                          ? "text-primary bg-primary/10"
                          : "text-foreground hover:bg-secondary"
                      }`}
                    >
                      Herramientas
                      <ChevronDown className={`h-4 w-4 transition-transform ${isCapsulasOpen ? "rotate-180" : ""}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 mt-1 space-y-1">
                    <button
                      onClick={() => handleNavigation("/capsulas")}
                      className="text-left px-4 py-2 rounded-md text-sm font-medium text-foreground hover:bg-secondary w-full"
                    >
                      Ver todas las herramientas
                    </button>
                    {capsulas.slice(0, 6).map((capsula) => (
                      <button
                        key={capsula.id}
                        onClick={() => handleNavigation(`/capsulas/${capsula.id}`)}
                        className="text-left px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary w-full whitespace-normal leading-snug"
                      >
                        <capsula.icon className="inline-block w-4 h-4 mr-2" />
                        <span>{capsula.title}</span>
                      </button>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

                {/* Guías - Mobile */}
                <button
                  onClick={() => handleNavigation("/cursos", undefined)}
                   className={`text-left px-4 py-2.5 rounded-md font-medium transition-colors ${
                    isActive("/cursos")
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:bg-secondary"
                  }`}
                >
                  Guías
                </button>
              </nav>

              <Button 
                variant="hero" 
                className="w-full mt-4"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/servicios/diagnostico-ia');
                }}
              >
                Diagnóstico Gratis
              </Button>

              {/* Contact Info - Mobile */}
              <div className="mt-6 pt-4 space-y-3">
                <a 
                  href="tel:+34916651232" 
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <Phone size={16} />
                  <span>+34 916 651 232</span>
                </a>
                <a 
                  href="mailto:soporte@globaltis.com" 
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  <Mail size={16} />
                  <span>soporte@globaltis.com</span>
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
        </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
